import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 20000;

export default function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (err) {
      // Polling failure — log for diagnostics, do not toast (background).
      console.warn("[NotificationBell.load]", err?.response?.status || err?.message);
    }
  };

  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); return; }
    load();
    timerRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const clickItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
      } catch (err) {
        console.warn("[NotificationBell.markRead]", err?.response?.status || err?.message);
      }
      setUnread((u) => Math.max(0, u - 1));
      setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.link) nav(n.link);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
    } catch (err) {
      console.warn("[NotificationBell.markAll]", err?.response?.status || err?.message);
    }
    setUnread(0);
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button data-testid="notif-bell" variant="ghost" size="sm" className="relative rounded-full h-9 w-9 p-0">
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-[hsl(var(--brand-black))] text-[10px] font-extrabold grid place-items-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="font-semibold text-sm">Bildirimler</div>
          {unread > 0 && (
            <button onClick={markAll} data-testid="notif-mark-all" className="text-xs text-primary hover:underline flex items-center gap-1">
              <CheckCheck className="w-3 h-3" /> Tümünü okundu işaretle
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">Henüz bildirim yok.</div>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => clickItem(n)}
              data-testid={`notif-item-${n.id}`}
              className={`w-full text-left px-3 py-2.5 border-b last:border-0 hover:bg-accent transition-colors ${!n.read ? "bg-primary/5" : ""}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />}
                {n.read && <Check className="w-3 h-3 text-muted-foreground mt-1 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm leading-tight">{n.title}</div>
                  {n.body && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>}
                  <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString("tr-TR")}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
